import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import {
    DotLegalButton,
    DotLegalInformationTooltip,
    DotLegalMultiSelect,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalPaper,
    GridSizes,
    ITableHeader,
    useHistoryWithReferer,
} from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useTemplates } from "./Templates.hooks.ts";
import { TemplatesTableRowModel } from "./Templates.types.ts";
import Grid from "@mui/material/Grid2";
import { useUrlProvider } from "../../useUrlProvider.ts";
import CreateEditTemplateDialog from "../createEditTemplateDialog/CreateEditTemplateDialog.tsx";
import { FrameworkTemplateSaveModel } from "../createEditTemplateDialog/CreateEditTemplateDialog.types.ts";
import ComplianceAreaWrapper from "../../common/components/complianceAreaWrapper/ComplianceAreaWrapper.tsx";
import FrameworkTemplateStatusChip from "../FrameworkTemplateStatusChip.tsx";
import ToggleFrameworkTemplateStatusDialog from "../frameworkTemplateOverview/toggleFrameworkTemplateStatusDialog/ToggleFrameworkTemplateStatusDialog.tsx";
import { getFrameworkImage } from "../../framework/frameworks/Frameworks.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DotLegalTableWithControlsLocalized from "../../common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized.tsx";

export interface TemplatesProps {}

function Templates() {
    const historyWithReferer = useHistoryWithReferer();

    const { getFrameworkTemplateOverviewUrl, getSettingsUrl } = useUrlProvider();
    const {
        isSavingOrder,
        isLoading,
        data,
        refetch,
        searchedStatus,
        setSearchedStatus,
        showCreateTemplate,
        setShowCreateTemplate,
        templateToEdit,
        setTemplateToEdit,
        showToggleIsPublishedDialog,
        setShowToggleIsPublishedDialog,
        updateFrameworkOrder,
    } = useTemplates();

    const gotoOverview = (id: string) => {
        historyWithReferer.push(getFrameworkTemplateOverviewUrl(id));
    };

    function getHeaders() {
        let headers = Array<ITableHeader<TemplatesTableRowModel>>();
        headers.push({ property: "order", text: "order", align: "left", showOnMobile: true });
        headers.push({ property: "name", text: "Framework", align: "left", showOnMobile: true });
        headers.push({ property: "complianceAreas", text: "Compliance Areas", align: "left", showOnMobile: true });
        headers.push({ property: "numberOfControls", text: "Controls", align: "left", showOnMobile: true });
        headers.push({ property: "numberOfAnnualWheelActivities", text: "Annual Wheel Activities", align: "left", showOnMobile: true });
        headers.push({ property: "isPublished", text: "Status", align: "left", showOnMobile: true });
        headers.push({ property: "templatePlanRequirements", text: "Required plans", align: "left", showOnMobile: true });

        return headers;
    }

    function onDragEnd(result: DropResult) {
        // dropped outside the list
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }

        const rows = document.querySelectorAll("[data-id]");

        const targetRow = rows[result.destination.index] as HTMLElement;
        const targetId = targetRow?.dataset.id;

        updateFrameworkOrder(result.draggableId, targetId!);
    }

    function getSearchField() {
        const gridSizes: GridSizes = {
            xs: 6,
            sm: 4,
            md: 2,
            lg: 2,
        };

        return (
            <React.Fragment>
                <Grid size={{ ...gridSizes }}>
                    <DotLegalMultiSelect
                        onChange={setSearchedStatus}
                        chosenOptions={searchedStatus}
                        label={"Status"}
                        placeholder={"Status"}
                        isLoading={false}
                        options={[
                            { id: "true", name: "Active" },
                            { id: "false", name: "Draft" },
                        ]}
                        noMargin
                        noOptionsLabel={""}
                    />
                </Grid>
            </React.Fragment>
        );
    }

    return (
        <>
            <DotLegalPageHeader
                breadCrumbs={[{ name: "Framework templates" }]}
                rightComponent={
                    <DotLegalButton buttonType="primary" size="medium" onClick={() => setShowCreateTemplate(true)}>
                        Create framework template
                    </DotLegalButton>
                }
            />

            <DotLegalPaper>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId={"frameworkTemplates"} direction={"vertical"}>
                        {(provided, snapshotDropable) => {
                            return (
                                <Box ref={provided.innerRef} {...provided.droppableProps}>
                                    <DotLegalTableWithControlsLocalized
                                        getUserSpecificPageLength={() => 3000}
                                        headers={getHeaders()}
                                        extraControls={getSearchField()}
                                        defaultOrderBy={"order"}
                                        hasOverflowMenu
                                        dragNDropPlaceholder={provided.placeholder}
                                        data={data ?? []}
                                        renderRow={(row, index) => (
                                            <Draggable key={row.id} draggableId={row.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <TableRow
                                                        data-id={row.id}
                                                        inactive={!row.isPublished}
                                                        onClick={() => gotoOverview(row.id)}
                                                        key={row.id + row.id}
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        sx={[
                                                            { ...provided.draggableProps.style },
                                                            (theme) => ({
                                                                ...(snapshot.isDragging && {
                                                                    background: theme.palette.primary.light,
                                                                    borderRadius: "10px",
                                                                    display: "table",
                                                                    "& .answerOptionDrag": {
                                                                        visibility: "visible",
                                                                    },
                                                                }),
                                                            }),
                                                        ]}
                                                    >
                                                        <TableCell>
                                                            <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row", gap: 1 }}>
                                                                <Box {...provided.dragHandleProps} sx={{ display: "flex" }}>
                                                                    <DragIndicatorIcon
                                                                        className="answerOptionDrag"
                                                                        sx={(theme) => ({
                                                                            visibility: isSavingOrder ? "hidden" : "visibile",
                                                                            fill: theme.palette.grey[500],
                                                                            height: 20,
                                                                            outline: "none",
                                                                            "&:hover": {
                                                                                cursor: "grab",
                                                                            },
                                                                        })}
                                                                    />
                                                                </Box>
                                                                {row.order}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row", gap: 1 }}>
                                                                <Box
                                                                    component={"img"}
                                                                    sx={(theme) => ({
                                                                        width: 30,
                                                                    })}
                                                                    src={getFrameworkImage(row.name)}
                                                                />

                                                                <Box
                                                                    sx={(theme) => ({
                                                                        fontWeight: 600,
                                                                        color: theme.palette.primary.main,
                                                                        display: "flex",
                                                                        gap: 1,
                                                                        alignItems: "center",
                                                                    })}
                                                                >
                                                                    <Box>{row.name}</Box>{" "}
                                                                    <DotLegalInformationTooltip text={row?.description ?? undefined} small />
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box
                                                                sx={(theme) => ({
                                                                    display: "flex",
                                                                    gap: 1,
                                                                    "& .MuiTypography-root": { fontSize: theme.typography.pxToRem(12) },
                                                                })}
                                                            >
                                                                {row.complianceAreas.map((x) => (
                                                                    <ComplianceAreaWrapper complianceArea={x.name} key={row.id + x.id} />
                                                                ))}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">{row.numberOfControls}</TableCell>
                                                        <TableCell align="right">{row.numberOfAnnualWheelActivities}</TableCell>
                                                        <TableCell>
                                                            <FrameworkTemplateStatusChip isPublished={row.isPublished} />
                                                        </TableCell>
                                                        <TableCell>{row.templatePlanRequirementsString}</TableCell>
                                                        <TableCell align={"right"}>
                                                            <DotLegalOverflowMenu
                                                                items={[
                                                                    {
                                                                        menuItem: "Open",
                                                                        onClick: () => gotoOverview(row.id),
                                                                    },
                                                                    {
                                                                        menuItem: "Edit",
                                                                        onClick: () => {
                                                                            setTemplateToEdit(row);
                                                                        },
                                                                    },
                                                                    {
                                                                        menuItem: row.isPublished ? "Deactivate framework" : "Activate framework",
                                                                        onClick: () => {
                                                                            setShowToggleIsPublishedDialog(row);
                                                                        },
                                                                    },
                                                                ]}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </Draggable>
                                        )}
                                    />
                                </Box>
                            );
                        }}
                    </Droppable>
                </DragDropContext>
            </DotLegalPaper>

            {showCreateTemplate && <CreateEditTemplateDialog onCloseDialog={() => setShowCreateTemplate(false)} />}
            {templateToEdit && (
                <CreateEditTemplateDialog
                    onCloseDialog={() => setTemplateToEdit(undefined)}
                    onTemplateEdited={() => {
                        setTemplateToEdit(undefined);
                        refetch();
                    }}
                    editModel={{
                        templateId: templateToEdit.id,
                        model: new FrameworkTemplateSaveModel(
                            templateToEdit.complianceAreas.map((x) => x.id),
                            templateToEdit.templatePlanRequirements,
                            templateToEdit.languages
                        ),
                    }}
                />
            )}

            {showToggleIsPublishedDialog && (
                <ToggleFrameworkTemplateStatusDialog
                    isPublished={showToggleIsPublishedDialog.isPublished}
                    templateId={showToggleIsPublishedDialog.id}
                    templateName={showToggleIsPublishedDialog.name}
                    onCloseDialog={() => setShowToggleIsPublishedDialog(undefined)}
                    onToggleSuccess={() => {
                        refetch();
                        setShowToggleIsPublishedDialog(undefined);
                    }}
                />
            )}
        </>
    );
}

export default Templates;
